import React, { useEffect, useState } from "react";
import _ from "lodash";
import { useTransition, animated, easings } from "@react-spring/web";
import { ArtworkWithTexture } from "../../types";
import { useLocation } from "wouter";
import ArtworkPickerSwiper from "../../components/ArtworkPickerSwiper";
import { useSpring } from "@react-spring/three";
import ClosePickerButton from "../../components/buttons/ClosePickerButton";
import { useStore } from "../../store/store";

interface ArtworkSelectorViewProps {}

const ArtworkSelectorView: React.FC<ArtworkSelectorViewProps> = () => {
  const { audioManager, setSelectedTags } = useStore();
  const [showSideText, setShowSideText] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [artworks, setArtworks] = useState<ArtworkWithTexture[]>([]);
  const [endTransition, setEndTransition] = useState(false);
  const [endAnimationRunning, setEndAnimationRunning] = useState(false);
  const [selectedArtworksTags, setSelectedArtworksTags] = useState<
    (string[] | null)[]
  >([]);
  const [selectedArtworkIds, setSelectedArtworkIds] = useState<string[]>([]);
  const isSwiperSelected = (artworks: ArtworkWithTexture[]) =>
    artworks.some((artwork) => selectedArtworkIds.includes(artwork.uuid));

  const [, navigate] = useLocation();

  const fetchArtPieces = async () => {
    try {
      const response = await fetch(
        "https://admin.collectieconnectie.nl/api/v1/artworks",
      );
      const data = await response.json();
      const fetchedArtworks = _.slice(data.collection.data, 0, 60);
      // @ts-ignore
      setArtworks(fetchedArtworks);
      setShowSideText(true);
    } catch (error) {
      console.error("Failed to fetch artworks:", error);
    }
  };

  useEffect(() => {
    fetchArtPieces().then(() => {});
  }, []);

  const selectArtworkTags = (
    index: number,
    tags: string[] | null,
    uuid: string,
  ) => {
    setSelectedArtworksTags((prev) => {
      const newTags = [...prev];
      newTags[index] = tags;
      return newTags;
    });
    if (tags) {
      setSelectedArtworkIds((prev) => {
        const newIds = [...prev];
        newIds[index] = uuid;
        return newIds;
      });
    }
  };

  const finishSelection = () => {
    audioManager!.play("click");
    audioManager!.play("background");
    const uniqueTags = _.uniq(
      _.flatten(selectedArtworksTags.filter(Boolean) as string[][]),
    );

    // Update the store with selected tags
    setSelectedTags(uniqueTags);

    setEndAnimationRunning(true);
  };

  // Animation transitions
  const transitions = useTransition(!endTransition, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 500 },
  });

  const artworkChunks = _.chunk(artworks, 20);

  const sideTextAnimation = useSpring({
    opacity: showSideText ? (endAnimationRunning ? 0 : 1) : 0,
    transform: showSideText ? "translateX(0)" : "translateX(150px)",
    config: { duration: 1000 },
  });

  const closeButtonFadeStyles = useSpring({
    opacity: endAnimationRunning ? 0 : 1,
    config: { duration: 1000 },
  });

  const sliderTransformAnimation = useSpring({
    transform: endAnimationRunning ? "translateX(15%)" : "translateX(0)",
    scale: endAnimationRunning ? 0.1 : 1,
    config: { duration: 2000 },
  });

  const sliderScaleAnimation = useSpring({
    scale: endAnimationRunning ? 0 : 1,
    config: { duration: 1000, easing: easings.easeInOutBack },
    delay: 2000,
    onRest: () => {
      setEndTransition(true);
      navigate("/collection");
    },
  });

  return transitions(
    (styles, item) =>
      item && (
        <animated.div
          style={styles}
          className="z-10 min-h-screen md:overflow-hidden md:h-full w-full absolute top-0 left-0 flex items-center justify-center backdrop--tinted"
        >
          <div className="mx-auto py-6 md:py-0 relative no-scrollbar min-h-screen md:h-screen container">
            <div className={`md:grid md:grid-cols-4 h-full relative`}>
              {/* Side Text for Mobile */}
              <div className="flex flex-col justify-center md:hidden mt-10">
                <div className="text-lg leading-tight mb-8 text-white pl-4 pr-8">
                  Draai door de kunstwerken tot er drie staan die je het meest
                  aanspreken.
                </div>
              </div>

              <animated.div
                style={{
                  ...sliderTransformAnimation,
                  ...sliderScaleAnimation,
                }}
                className={
                  "flex flex-col transform transition-transform transition-scale md:grid md:grid-cols-3 md:col-span-3"
                }
              >
                {/* Swiper Components */}
                {artworkChunks.map((chunk, idx) => (
                  <div key={idx} className="text-white">
                    {chunk && (
                      <ArtworkPickerSwiper
                        artworks={chunk}
                        index={idx}
                        onSlidedTo={(tags, uuid) =>
                          selectArtworkTags(idx, tags, uuid)
                        }
                        isSelected={isSwiperSelected(chunk)}
                        endAnimationRunning={endAnimationRunning}
                      />
                    )}
                  </div>
                ))}
              </animated.div>

              {/* Side Text and Button */}
              <animated.div
                style={sideTextAnimation}
                className="flex flex-col justify-center text-column transform transition-transform transition-opacity opacity-0"
              >
                <div className="text-white flex flex-col items-center md:items-start">
                  <div className="text-2xl hidden md:flex">
                    Draai door de kunstwerken tot er drie staan die je het meest
                    aanspreken.
                  </div>

                  <button
                    onClick={finishSelection}
                    className="flex mt-8 justify-center items-center p-1 border-2 border-white/50 rounded-full text-white text-sm hover:border-seafoamGreen transition-colors min-h-10 px-3"
                  >
                    <div className="flex-grow text-center">
                      Dit zijn mijn favorieten
                    </div>
                  </button>
                </div>
              </animated.div>
            </div>
          </div>
          <animated.div
            style={closeButtonFadeStyles}
            className={
              "absolute top-4 md:top-6 right-4 md:right-8 flex flex-col gap-3 items-end z-30 transform transition-opacity"
            }
          >
            <ClosePickerButton
              onClick={() => {
                setShowCloseModal(!showCloseModal);
              }}
            />
            {showCloseModal && (
              <div className="absolute top-12 right-0 border-gray-300 border-2 rounded-lg w-64 text-white bg-black flex-col p-4 text-sm flex sort-dropdown">
                <div className="flex flex-col gap-3 items-center">
                  <span className="flex items-center text-center justify-center text-lg">
                    Weet je zeker dat je wilt stoppen?
                  </span>
                  <button
                    className="button flex justify-center content-center items-center min-h-10 px-3 border-white/50 border-2 rounded-full text-white text-sm hover:border-sunsetCoral transition-colors"
                    onClick={() => setShowCloseModal(false)}
                  >
                    <span className="d-icon d-close flex !text-lg mr-2"></span>
                    <div className="flex-grow text-center">
                      Nee, ik wil doorgaan
                    </div>
                  </button>
                  <button
                    className="button flex justify-center content-center items-center min-h-10 px-3 border-white/50 border-2 rounded-full text-white text-sm hover:border-seafoamGreen transition-colors"
                    onClick={() => {
                      setSelectedArtworksTags([]);
                      audioManager!.play("click");
                      audioManager!.play("background");
                      navigate("/collection");
                    }}
                  >
                    <span className="d-icon d-check flex !text-lg mr-2"></span>
                    <div className="flex-grow text-center">
                      Ja, ik wil stoppen
                    </div>
                  </button>
                </div>
              </div>
            )}
          </animated.div>
        </animated.div>
      ),
  );
};

export default ArtworkSelectorView;
